// Groups a flat influx field list into the prefix-based sections the panel renders
// (Spindle / one per axis / Lubrication / Other), matching the design doc §4 grouping.

export const SIGNAL_GROUP_ORDER = ['spindle', 'x', 'y', 'z', 'b', 'c', 'lubrication', 'other'] as const;
export type SignalGroupKey = (typeof SIGNAL_GROUP_ORDER)[number];

const GROUP_PREFIXES: Array<{ key: Exclude<SignalGroupKey, 'other'>; prefix: string }> = [
  { key: 'spindle', prefix: 'SPINDLE_' },
  { key: 'x', prefix: 'X_' },
  { key: 'y', prefix: 'Y_' },
  { key: 'z', prefix: 'Z_' },
  { key: 'b', prefix: 'B_' },
  { key: 'c', prefix: 'C_' },
  { key: 'lubrication', prefix: 'LUB_' },
];

export interface SignalGroup {
  key: SignalGroupKey;
  signals: string[];
}

export function groupSignals(signals: string[]): SignalGroup[] {
  const used = new Set<string>();

  const groups: SignalGroup[] = GROUP_PREFIXES.map(({ key, prefix }) => {
    const matched = signals.filter((tag) => tag.startsWith(prefix));
    for (const tag of matched) used.add(tag);
    return { key, signals: matched };
  }).filter((group) => group.signals.length > 0);

  const other = signals.filter((tag) => !used.has(tag));
  if (other.length > 0) groups.push({ key: 'other', signals: other });

  return groups;
}

// Default-checked when present — the panel's initial selection for a freshly loaded
// machine (min-1 rule falls back to the first fetched signal if none of these exist).
export const DEFAULT_CORE_SIGNALS = ['SPINDLE_PWR', 'X_FOLLERR', 'Y_FOLLERR', 'Z_FOLLERR'];

export function defaultCheckedSignals(signals: string[]): string[] {
  const core = DEFAULT_CORE_SIGNALS.filter((tag) => signals.includes(tag));
  if (core.length > 0) return core;
  return signals.length > 0 ? [signals[0]] : [];
}

export const SIGNALS_MIN = 1;
export const SIGNALS_MAX = 15;
